import errorHandler from '@/services/errorHandler'
/**
 * Утилита для воспроизведения звука уведомлений
 * Генерирует приятный звук уведомления с помощью Web Audio API
 */

class NotificationSound {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    
    // Загружаем настройку из localStorage
    const savedSetting = localStorage.getItem('notification_sound_enabled');
    if (savedSetting !== null) {
      this.enabled = savedSetting === 'true';
    }
  }

  /**
   * Инициализация Audio Context (только после взаимодействия пользователя)
   */
  async initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    // Проверяем состояние AudioContext и возобновляем, если он suspended
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (error) {
        errorHandler.handle(error, { showDialog: false, logToConsole: false });
      }
    }
  }

  /**
   * Воспроизводит приятный звук уведомления
   * Двойной тон (C5 -> E5) с плавным затуханием
   */
  async play() {
    if (!this.enabled) {
      return;
    }

    try {
      await this.initAudioContext();
      
      // Проверяем, что AudioContext активен
      if (!this.audioContext || this.audioContext.state !== 'running') {
        return;
      }
      
      const context = this.audioContext;
      const currentTime = context.currentTime;

      // Создаем осциллятор для первого тона (C5 - 523.25 Hz)
      const oscillator1 = context.createOscillator();
      const gainNode1 = context.createGain();
      
      oscillator1.connect(gainNode1);
      gainNode1.connect(context.destination);
      
      oscillator1.type = 'sine'; // Мягкий синусоидальный тон
      oscillator1.frequency.setValueAtTime(523.25, currentTime);
      
      // Плавное нарастание и затухание (envelope)
      gainNode1.gain.setValueAtTime(0, currentTime);
      gainNode1.gain.linearRampToValueAtTime(0.3, currentTime + 0.02); // Attack
      gainNode1.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.3); // Decay
      
      oscillator1.start(currentTime);
      oscillator1.stop(currentTime + 0.3);

      // Создаем осциллятор для второго тона (E5 - 659.25 Hz) с задержкой
      const oscillator2 = context.createOscillator();
      const gainNode2 = context.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(context.destination);
      
      oscillator2.type = 'sine';
      oscillator2.frequency.setValueAtTime(659.25, currentTime + 0.15);
      
      gainNode2.gain.setValueAtTime(0, currentTime + 0.15);
      gainNode2.gain.linearRampToValueAtTime(0.25, currentTime + 0.17);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.45);
      
      oscillator2.start(currentTime + 0.15);
      oscillator2.stop(currentTime + 0.45);
      
    } catch (error) {
      errorHandler.handle(error, { showDialog: false, logToConsole: false });
      }
  }

  /**
   * Включает звук уведомлений
   */
  enable() {
    this.enabled = true;
    localStorage.setItem('notification_sound_enabled', 'true');
  }

  /**
   * Отключает звук уведомлений
   */
  disable() {
    this.enabled = false;
    localStorage.setItem('notification_sound_enabled', 'false');
  }

  /**
   * Переключает звук уведомлений
   */
  toggle() {
    if (this.enabled) {
      this.disable();
    } else {
      this.enable();
    }
    return this.enabled;
  }

  /**
   * Проверяет, включен ли звук
   */
  isEnabled() {
    return this.enabled;
  }
}

// Экспортируем синглтон
export default new NotificationSound();

